/**
 * Email Preview Dialog Component
 *
 * Full-screen dialog for previewing email campaigns with actual customer data
 * Features:
 * - Device size toggles (Mobile, Tablet, Desktop)
 * - Large scrollable email preview
 * - Collapsible side panel with variable resolution details
 * - Progressive disclosure for technical information
 *
 * Phase 4.2c - UX Improvements
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { Monitor, Smartphone, Tablet, Camera, Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { capturePreviewDialogScreenshot } from '@/utils/capturePreviewDialog';
import { processTemplatePreview } from '@/utils/processTemplateImage';
import { uploadTemplatePreview, deleteOldTemplatePreviews } from '@/services/templatePreviewService';
import EmailService from '@/api/email';

type DeviceSize = 'mobile' | 'tablet' | 'desktop';

interface PreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resolvedPreview: {
    html: string;
    subject: string;
    resolved_variables: Record<string, Record<string, string>>;
    customer: {
      id: string;
      email: string;
      first_name: string;
      last_name: string;
    };
  };
  // Optional template context for capturing preview (system templates)
  template?: {
    id: string;
    template_name: string;
    is_system_template?: boolean;
  };
  userRole?: string;
  onPreviewCaptured?: () => void; // Callback after successful capture
}

const PreviewDialog: React.FC<PreviewDialogProps> = ({
  open,
  onOpenChange,
  resolvedPreview,
  template,
  userRole,
  onPreviewCaptured
}) => {
  const [deviceSize, setDeviceSize] = useState<DeviceSize>('desktop');
  const [capturingPreview, setCapturingPreview] = useState(false);

  // Show capture button only for super admins on system templates
  const canCapturePreview = template?.is_system_template && userRole === 'super_admin';

  // Debug logging to help troubleshoot visibility
  console.log('[PreviewDialog] Capture button check:', {
    hasTemplate: !!template,
    templateId: template?.id,
    templateName: template?.template_name,
    isSystemTemplate: template?.is_system_template,
    userRole: userRole,
    canCapturePreview: canCapturePreview
  });

  // Handle preview capture from current preview dialog
  const handleCapturePreview = async () => {
    if (!template) return;

    try {
      setCapturingPreview(true);

      console.log('[PreviewDialog] Capturing preview for template:', template.template_name);

      // Wait a moment for any rendering to complete
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Capture screenshot
      const screenshot = await capturePreviewDialogScreenshot(500);

      // Process image
      const processed = await processTemplatePreview(screenshot);

      // Upload to storage
      const publicUrl = await uploadTemplatePreview(template.id, processed);

      // Update template record
      await EmailService.updateTemplate(template.id, {
        preview_image_url: publicUrl
      });

      // Cleanup old previews (non-blocking)
      deleteOldTemplatePreviews(template.id).catch(err => {
        console.warn('[PreviewDialog] Cleanup failed (non-critical):', err);
      });

      toast({
        title: 'Preview captured!',
        description: `Preview image saved for "${template.template_name}"`,
      });

      // Call callback to refresh templates list
      if (onPreviewCaptured) {
        onPreviewCaptured();
      }

      console.log('[PreviewDialog] Preview capture complete');

    } catch (error) {
      console.error('[PreviewDialog] Preview capture failed:', error);

      toast({
        title: 'Capture failed',
        description: error instanceof Error ? error.message : 'Failed to capture preview',
        variant: 'destructive'
      });
    } finally {
      setCapturingPreview(false);
    }
  };

  // Get device width based on selected size
  const getDeviceWidth = () => {
    switch (deviceSize) {
      case 'mobile':
        return 'w-[375px]'; // Mobile: 375px fixed width
      case 'tablet':
        return 'w-[768px]'; // Tablet: 768px fixed width
      case 'desktop':
        return 'w-full'; // Desktop: full width
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl h-[90vh] p-0 gap-0 flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle>
            Preview for: {resolvedPreview.customer.first_name}{' '}
            {resolvedPreview.customer.last_name} ({resolvedPreview.customer.email})
          </DialogTitle>
          <DialogDescription>
            <strong>Subject:</strong> {resolvedPreview.subject}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-0 flex-1 overflow-hidden">
          {/* Main Preview Panel - 70% */}
          <div className="flex-[7] flex flex-col bg-gray-100">
            {/* Email Preview Iframe */}
            <div className="flex-1 flex flex-col items-center justify-start overflow-auto p-6">
              <div
                className={cn(
                  'border rounded-lg overflow-hidden bg-white shadow-lg transition-all duration-300',
                  'h-full',
                  getDeviceWidth()
                )}
              >
                <iframe
                  srcDoc={resolvedPreview.html}
                  className="w-full h-full border-0"
                  title="Email Preview"
                  sandbox="allow-same-origin"
                  style={{
                    minHeight: '600px',
                  }}
                />
              </div>
            </div>
          </div>

          {/* Side Panel - 30% */}
          <div className="flex-[3] border-l flex flex-col">
            <ScrollArea className="flex-1 px-6 pt-6">
              <div className="space-y-4 pb-4">
                {/* Device Size Toggle */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">
                    Device Preview
                  </Label>
                  <div className="flex gap-2">
                    <Button
                      variant={deviceSize === 'mobile' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setDeviceSize('mobile')}
                      className="gap-2 flex-1"
                    >
                      <Smartphone className="h-4 w-4" />
                      Mobile
                    </Button>
                    <Button
                      variant={deviceSize === 'tablet' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setDeviceSize('tablet')}
                      className="gap-2 flex-1"
                    >
                      <Tablet className="h-4 w-4" />
                      Tablet
                    </Button>
                    <Button
                      variant={deviceSize === 'desktop' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setDeviceSize('desktop')}
                      className="gap-2 flex-1"
                    >
                      <Monitor className="h-4 w-4" />
                      Desktop
                    </Button>
                  </div>
                </div>

                {/* Variable Resolution - Collapsible Sections */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">
                    Variable Resolution
                  </Label>
                  <p className="text-xs text-muted-foreground mb-3">
                    How variables were replaced for this customer
                  </p>
                  <Accordion type="multiple" className="w-full">
                    {Object.entries(resolvedPreview.resolved_variables).map(
                      ([category, vars]) => {
                        const varEntries = Object.entries(vars);
                        if (varEntries.length === 0) return null;

                        return (
                          <AccordionItem key={category} value={category}>
                            <AccordionTrigger className="text-sm py-3">
                              <div className="flex items-center gap-2">
                                <span className="capitalize">{category}</span>
                                <Badge variant="secondary" className="text-xs">
                                  {varEntries.length}
                                </Badge>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent>
                              <div className="text-xs space-y-2 pb-2">
                                {varEntries.map(([variable, value]) => (
                                  <div
                                    key={variable}
                                    className={cn(
                                      'p-2 rounded-md bg-muted',
                                      category === 'dynamic' && 'break-all'
                                    )}
                                  >
                                    <code className="bg-white px-1 py-0.5 rounded text-[10px]">
                                      {variable}
                                    </code>
                                    <br />
                                    {category === 'dynamic' ? (
                                      <a
                                        href={value}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:underline text-[10px] mt-1 inline-block"
                                      >
                                        {value || 'N/A'}
                                      </a>
                                    ) : (
                                      <span className="font-medium mt-1 inline-block">
                                        {value || 'N/A'}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        );
                      }
                    )}
                  </Accordion>
                </div>
              </div>
            </ScrollArea>

            {/* Action Buttons - Bottom of Side Panel */}
            <div className="px-6 py-4 border-t space-y-2">
              {/* Capture Preview Button (Super Admin Only, System Templates) */}
              {canCapturePreview && (
                <Button
                  variant="default"
                  onClick={handleCapturePreview}
                  disabled={capturingPreview}
                  className="w-full"
                >
                  {capturingPreview ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Capturing...
                    </>
                  ) : (
                    <>
                      <Camera className="mr-2 h-4 w-4" />
                      Capture Preview for Template
                    </>
                  )}
                </Button>
              )}

              {/* Close Button */}
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="w-full"
                disabled={capturingPreview}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PreviewDialog;
